/**
 * Le récapitulatif, avant d'enregistrer.
 *
 * Il existe pour une raison simple : c'est le seul écran où le parent voit ce
 * qu'il a déclaré. Dans le parcours d'origine, la dernière étape enregistrait
 * directement — on découvrait ses réponses en rouvrant la fiche, c'est-à-dire
 * trop tard pour corriger sans se demander si on avait bien compris la
 * question.
 *
 * Chaque bloc renvoie à son étape. Rien ne s'y modifie champ par champ : un
 * champ modifiable ici serait un troisième endroit où la même règle de
 * validation devrait vivre.
 *
 * ## Le même récapitulatif sur la fiche de l'enfant
 *
 * La page `/kids/:id` l'affiche en mode `consultation` : sans phrase
 * d'introduction ni bouton final, avec le nombre d'informations à compléter
 * par bloc. « Modifier » y ouvre l'étape à la place du bloc (`enModification`,
 * `edition`), avec les écrans de la création. Le parent retrouve ce qu'il a
 * rempli, là où il l'a rempli.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import clsx from 'clsx';
import { DESCRIPTIONS } from '../../../lib/droitImage';
import type { AuthorizedPerson } from '../../../stores/authorizedPersonsStore';
import type { School } from '../../../stores/schoolStore';
import { Navigation } from './champs';
import { BadgeSection } from './Completude';
import { PHRASE_NATATION, nrnRetenu } from './fiche';
import type { Etape, Fiche } from './fiche';

/** Une ligne « intitulé → valeur » du récapitulatif. */
const Ligne = ({ titre, children }: { titre: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:gap-4">
    <dt className="text-sm text-gray-500 sm:w-56 sm:flex-shrink-0">{titre}</dt>
    <dd className="text-sm text-gray-900">{children}</dd>
  </div>
);

const Rien = () => <span className="text-gray-400">Non renseigné</span>;

/** Ce que la page de l'enfant passe au récapitulatif pour l'afficher en consultation. */
export interface Consultation {
  /** Les informations bloquantes à compléter, par étape. */
  manques: Partial<Record<Etape, number>>;
  /** L'étape ouverte en modification, s'il y en a une. */
  enModification: Etape | null;
  /** Le formulaire de l'étape ouverte, rendu à la place de son bloc. */
  edition: React.ReactNode;
}

const ContexteConsultation = createContext<Consultation | null>(null);

const Section = ({
  titre,
  etape,
  onModifier,
  children,
}: {
  titre: string;
  etape: Etape;
  onModifier: (e: Etape) => void;
  children: React.ReactNode;
}) => {
  const consultation = useContext(ContexteConsultation);
  const ouverte = consultation?.enModification === etape;
  const autreOuverte = !!consultation?.enModification && !ouverte;
  const manques = consultation?.manques[etape] ?? 0;

  return (
    <section
      id={`etape-${etape}`}
      className={clsx(
        'scroll-mt-24 rounded-xl border p-4 sm:p-5',
        ouverte ? 'border-primary-300 bg-white shadow-md ring-1 ring-primary-200' : 'border-gray-200 bg-white',
      )}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <h3 className="flex flex-wrap items-center gap-2 font-semibold text-gray-900">
          {titre}
          {!ouverte && manques > 0 && <BadgeSection manquants={manques} />}
        </h3>
        {!ouverte && (
          <button
            type="button"
            onClick={() => onModifier(etape)}
            disabled={autreOuverte}
            title={autreOuverte ? 'Enregistrez ou annulez d’abord la partie ouverte.' : undefined}
            className="inline-flex min-h-[44px] flex-shrink-0 items-center text-sm font-medium text-primary-700 hover:text-primary-800 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            <Pencil className="mr-1 h-3.5 w-3.5" />
            {manques > 0 ? 'Compléter' : 'Modifier'}
          </button>
        )}
      </div>
      {ouverte ? <div className="pt-2">{consultation?.edition}</div> : <dl className="divide-y divide-gray-100">{children}</dl>}
    </section>
  );
};

const Recapitulatif = ({
  fiche,
  photo,
  photoExistante,
  ecoles,
  personnes,
  onModifier,
  onPrecedent,
  onEnregistrer,
  enCours,
  libelleEnregistrer = 'Enregistrer la fiche',
  classe,
  consultation,
}: {
  fiche: Fiche;
  photo: File | null;
  /** L'URL signée de la photo déjà en banque, s'il y en a une. */
  photoExistante?: string | null;
  ecoles: School[];
  personnes: AuthorizedPerson[];
  onModifier: (e: Etape) => void;
  /* Absents en consultation : la page de l'enfant n'a pas de bouton final. */
  onPrecedent?: () => void;
  onEnregistrer?: () => void;
  enCours?: boolean;
  /* La verification dit « Tout est exact », la creation « Enregistrer ». */
  libelleEnregistrer?: string;
  /** La classe telle qu'elle s'affiche ; `undefined` quand l'école n'en demande pas. */
  classe?: React.ReactNode;
  /** Sur la page de l'enfant : sans introduction ni bouton final, modifiable bloc par bloc. */
  consultation?: Consultation;
}) => {
  const { identite, sante, vie, sorties, autorisations } = fiche;

  /*
    L'apercu du fichier choisi, cree une fois et libere avec lui. Un
    `URL.createObjectURL` appele en plein rendu en fabriquerait un nouveau a
    chaque frappe, sans jamais en liberer aucun.
  */
  const [apercu, setApercu] = useState<string | null>(null);
  useEffect(() => {
    if (!photo) {
      setApercu(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const vignette = apercu || photoExistante || null;
  const prenomLisible = identite.prenom.trim() || 'votre enfant';

  const nomEcole =
    identite.ecole === 'other'
      ? identite.ecoleAutre
      : ecoles.find((e) => e.id === identite.ecole)?.name || identite.ecole;

  const naissance = identite.dateNaissance
    ? new Date(identite.dateNaissance).toLocaleDateString('fr-BE')
    : '';

  const choisies = personnes.filter((p) => sorties.personnesIds.includes(p.id));

  return (
    <ContexteConsultation.Provider value={consultation ?? null}>
    <div className={consultation ? 'space-y-4' : 'space-y-6'}>
      {!consultation && (
        <p className="text-gray-600">
          Vérifiez ce que vous avez indiqué. Vous pourrez tout modifier plus tard depuis la fiche de
          votre enfant.
        </p>
      )}

      <Section titre="Votre enfant" etape="identite" onModifier={onModifier}>
        <Ligne titre="Nom et prénom">
          {identite.prenom} {identite.nom}
        </Ligne>
        <Ligne titre="Date de naissance">{naissance || <Rien />}</Ligne>
        <Ligne titre="Registre national">
          {nrnRetenu(identite) || <span className="text-amber-600">Non renseigné — pas d’attestation fiscale</span>}
        </Ligne>
        <Ligne titre="Adresse">
          {identite.adresse}, {identite.cPostal} {identite.localite}
        </Ligne>
        <Ligne titre="École">{nomEcole || <Rien />}</Ligne>
        {classe !== undefined && <Ligne titre="Classe">{classe}</Ligne>}
        {/*
          Le nom du fichier ne disait rien a personne, et une fiche dont la photo
          est deja en banque affichait « Non renseigne » — le parent croyait
          l'avoir perdue. La vignette repond a la seule question posee : y en
          a-t-il une, et laquelle.
        */}
        <Ligne titre="Photo">
          {vignette ? (
            <span className="flex items-center gap-2">
              <span className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-gray-100">
                <img src={vignette} alt="" className="h-full w-full object-cover" />
              </span>
              <span className="text-gray-600">
                {photo ? 'Nouvelle photo' : 'Photo enregistrée'}
              </span>
            </span>
          ) : (
            <Rien />
          )}
        </Ligne>
      </Section>

      <Section titre="Santé et alimentation" etape="sante" onModifier={onModifier}>
        <Ligne titre="Informations médicales">{sante.infosMedicales || <Rien />}</Ligne>
        <Ligne titre="Antécédents">{sante.antecedents || <Rien />}</Ligne>
        <Ligne titre="Médicaments">
          {sante.medication ? (
            <>
              {sante.medicationDetails}
              <span className="block text-gray-500">
                {sante.medicationAutonomie
                  ? 'Peut le prendre sans l’aide d’un adulte.'
                  : 'A besoin d’un adulte.'}
              </span>
            </>
          ) : (
            'Aucun'
          )}
        </Ligne>
        <Ligne titre="Vaccin tétanos">{sante.tetanos ? 'À jour' : 'Non / inconnu'}</Ligne>
        <Ligne titre="Médecin">
          {sante.medecinNom || sante.medecinTel ? (
            `${sante.medecinNom} ${sante.medecinTel}`.trim()
          ) : (
            <Rien />
          )}
        </Ligne>
        <Ligne titre="Allergies">
          {sante.allergies ? (
            <>
              {sante.allergiesDetails}
              <span className="block text-gray-500">{sante.allergiesConsequences}</span>
            </>
          ) : (
            'Aucune'
          )}
        </Ligne>
        <Ligne titre="Régime">
          {sante.regimeSpecial ? sante.regimeDetails : 'Aucun régime particulier'}
        </Ligne>
      </Section>

      <Section titre="Sa journée en stage" etape="vie" onModifier={onModifier}>
        <Ligne titre="Activités">
          {vie.participeATout ? 'Participe à tout' : vie.restrictions}
        </Ligne>
        <Ligne titre="Natation">
          {/* La même phrase que dans l'étape : « bien » seul ne dit pas de quoi. */}
          {vie.natation ? PHRASE_NATATION[vie.natation](prenomLisible) : <Rien />}
          {vie.peurDeLEau && <span className="block text-gray-500">A peur de l’eau.</span>}
        </Ligne>
        <Ligne titre="Besoins particuliers">
          {vie.besoinsSpecifiques ? vie.situation : 'Aucun signalé'}
        </Ligne>
        <Ligne titre="Autre information">{vie.autresInfos || <Rien />}</Ligne>
      </Section>

      <Section titre="Qui vient le ou la chercher" etape="sorties" onModifier={onModifier}>
        {sorties.partSeul ? (
          <Ligne titre="Départ">
            Peut rentrer seul à partir de {sorties.heureDepart}
          </Ligne>
        ) : (
          <Ligne titre="Personnes autorisées">
            {choisies.length === 0 ? (
              <Rien />
            ) : (
              <ul>
                {choisies.map((p) => (
                  <li key={p.id}>
                    {p.first_name} {p.last_name}{' '}
                    <span className="text-gray-500">({p.relationship})</span>
                  </li>
                ))}
              </ul>
            )}
          </Ligne>
        )}
      </Section>

      <Section titre="Autorisations" etape="autorisations" onModifier={onModifier}>
        <Ligne titre="Soins et urgences">
          {autorisations.soins ? 'Accord donné' : <span className="text-red-600">Sans accord</span>}
        </Ligne>
        {DESCRIPTIONS.map((d) => (
          <Ligne key={d.cle} titre={`${d.lettre}. ${d.titre}`}>
            {autorisations.image[d.cle] === 'oui' ? (
              <span className="font-medium text-green-700">Oui</span>
            ) : autorisations.image[d.cle] === 'non' ? (
              <span className="text-gray-600">Non</span>
            ) : (
              <Rien />
            )}
            {d.cle === 'reseauxSociaux' && autorisations.image.reseauxSociaux === 'oui' && (
              <span className="block text-gray-500">De dos ou non reconnaissable</span>
            )}
          </Ligne>
        ))}
        <Ligne titre="Accord de l’autre parent">
          {autorisations.image.accordAutreParent ? 'Confirmé' : 'Non requis / non confirmé'}
        </Ligne>
      </Section>

      {!consultation && (
        <Navigation
          onPrecedent={onPrecedent}
          onSuivant={onEnregistrer}
          libelleSuivant={libelleEnregistrer}
          enCours={enCours}
        />
      )}
    </div>
    </ContexteConsultation.Provider>
  );
};

export default Recapitulatif;
